import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';

import { Tooltip } from '@mui/material';
import ErrorIcon from '@mui/icons-material/Error';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

dayjs.extend(customParseFormat);

const EXCEL_SERIAL_MIN = 1;
const EXCEL_SERIAL_MAX = 109575;

const isExcelSerial = (str) => {
    const n = Number(str.trim());
    return Number.isInteger(n) && n >= EXCEL_SERIAL_MIN && n <= EXCEL_SERIAL_MAX;
};

export const isValidDate = (dateStr) => {
    if (!dateStr) return false;

    const cleanDateStr = dateStr.replace(/\s*(a\.?m\.?|p\.?m\.?)\s*/gi, '').trim();

    if (isExcelSerial(cleanDateStr)) return false;

    const date = dayjs(cleanDateStr);

    return date.isValid();
};

export const sanitizePrice = (priceStr) => {
    if (!priceStr) return null;

    // Only remove currency symbols and keep other characters
    const sanitized = String(priceStr)
        .replace(/[$€£¥₹₽₩₴₦₱₲₺₼₸]/g, '')
        .replace(/,/g, '');

    // Check if the string contains numbers
    if (!/\d/.test(sanitized)) return null;

    // For date-like strings, time formats, and sentences with numbers, return null
    if (
        /\d{4}-\d{2}-\d{2}/.test(sanitized) || // Date format check
        /\d{2}:\d{2}:\d{2}/.test(sanitized) || // Time format check
        /AM|PM/i.test(sanitized) || // AM/PM check
        /\d+\s+[a-z]+/i.test(sanitized) || // Number followed by text
        /[a-z]+\s+\d+/i.test(sanitized)
    ) {
        // Text followed by number
        return null;
    }

    // If the string contains multiple words but also contains a number, it's likely not a price
    if (sanitized.trim().split(/\s+/).length > 1) {
        return null;
    }

    // Try to extract just the price portion if it exists
    const priceMatch = sanitized.match(/^[^\d]*?([-+]?\d+\.?\d*)[^\d]*?$/);
    if (priceMatch) {
        const value = parseFloat(priceMatch[1]);
        return isNaN(value) ? null : value;
    }

    // Fallback: let parseFloat try to extract a number from the beginning
    const value = parseFloat(sanitized);
    return isNaN(value) ? null : value;
};

export const extendedHeaders = (headers = [], showStatus = false) => {
    if (!showStatus) return headers;
    return [
        {
            id: 'status',
            label: 'Status',
            sort: false,
            render: (row) =>
                row?._status?.valid ? (
                    <Tooltip title="Valid row">
                        <CheckCircleIcon color="success" />
                    </Tooltip>
                ) : (
                    <Tooltip
                        title={
                            <span
                                dangerouslySetInnerHTML={{
                                    __html: row?._status?.issues
                                        ?.map((issue) => issue.replace('*', ''))
                                        .join(' <br /> '),
                                }}
                            />
                        }
                    >
                        <ErrorIcon color="error" />
                    </Tooltip>
                ),
        },
        ...headers,
    ];
};

export const statusOptions = [
    { label: 'All', value: 'all' },
    { label: 'Valid Rows', value: 'valid' },
    { label: 'Invalied Rows', value: 'invalid' },
];
